import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { shouldOfferFrench } from "@/lib/geo-detect";
import { trackEvent } from "@/lib/posthog";

/**
 * QuebecLanguageBanner · top banner offering French to likely-Quebec visitors.
 *
 * Shows only when ALL of:
 *   1. User has NOT explicitly chosen a language yet (no cr-language in
 *      localStorage — i18next writes that key on first changeLanguage call)
 *   2. User has not previously dismissed this banner (cr-fr-banner-dismissed)
 *   3. shouldOfferFrench() returns true (Cloudflare geo CA+QC, or navigator
 *      indicates fr-*, or the strong-signal combos in geo-detect.ts)
 *   4. The current i18n language is not already fr
 *
 * Three actions: Oui (switch to French + record choice), Non (dismiss
 * permanently as English), Plus tard (dismiss this session only).
 */

const DISMISS_KEY = "cr-fr-banner-dismissed";
const LANGUAGE_KEY = "cr-language";

type BannerState = "checking" | "hidden" | "visible";

export function QuebecLanguageBanner() {
  const { i18n } = useTranslation();
  const [state, setState] = useState<BannerState>("checking");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Already chose a language explicitly? Don't bother.
      if (typeof localStorage !== "undefined" && localStorage.getItem(LANGUAGE_KEY)) {
        setState("hidden");
        return;
      }
      // Permanently dismissed?
      if (typeof localStorage !== "undefined" && localStorage.getItem(DISMISS_KEY)) {
        setState("hidden");
        return;
      }
      // Already showing French? No banner needed.
      if (i18n.language?.toLowerCase().startsWith("fr")) {
        setState("hidden");
        return;
      }

      const offer = await shouldOfferFrench();
      if (!cancelled) {
        setState(offer ? "visible" : "hidden");
        if (offer) {
          trackEvent("qc_banner_shown", { current_language: i18n.language });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  if (state !== "visible") return null;

  const handleAccept = () => {
    trackEvent("qc_banner_accepted", { from: i18n.language });
    i18n.changeLanguage("fr");
    setState("hidden");
  };

  const handleDecline = () => {
    trackEvent("qc_banner_declined");
    // Treat "Non" as an explicit English preference. Persist so we never ask again.
    try {
      localStorage.setItem(LANGUAGE_KEY, "en");
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // localStorage unavailable (private mode) — banner just won't suppress
    }
    setState("hidden");
  };

  const handleLater = () => {
    trackEvent("qc_banner_later");
    // Session-only dismiss. The banner can return next visit.
    setState("hidden");
  };

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-primary text-[#0A0A0B] px-4 py-3 shadow-md"
      role="region"
      aria-label="Language preference"
    >
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
        <p className="font-medium leading-snug text-center sm:text-left">
          <span className="mr-1.5" aria-hidden="true">🇨🇦</span>
          Bonjour. Cipher &amp; Row est disponible en français. Continuer en français?
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleAccept}
            className="px-3 py-1.5 rounded-md bg-[#0A0A0B] text-primary text-xs font-semibold hover:bg-[#0A0A0B]/90 transition-colors"
          >
            Oui, en français
          </button>
          <button
            type="button"
            onClick={handleLater}
            className="px-3 py-1.5 rounded-md text-xs font-medium hover:bg-[#0A0A0B]/10 transition-colors"
          >
            Plus tard
          </button>
          <button
            type="button"
            onClick={handleDecline}
            aria-label="Continue in English"
            className="p-1.5 rounded-md hover:bg-[#0A0A0B]/10 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
